import { z } from 'zod';
import { listQuerySchema } from '../common/pagination';
import { timerProjectKindSchema } from '../time-tracking/master-data.schema';

export const travelStatusSchema = z.enum(['PENDING', 'APPROVED', 'REJECTED']);
export type TravelStatus = z.infer<typeof travelStatusSchema>;

export const TRAVEL_STATUS_LABELS: Record<TravelStatus, string> = {
  PENDING: 'Ausstehend',
  APPROVED: 'Genehmigt',
  REJECTED: 'Abgelehnt',
};

/** Wanduhrzeit `HH:mm`. */
const timeString = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Uhrzeit im Format HH:mm erwartet');

/**
 * Stundendifferenz zweier `HH:mm`-Zeiten eines Reisetags. Liegt das Ende vor dem
 * Start, endet der Tag nach Mitternacht (18:00–01:00 = 7 h) — dieselbe Regel wie
 * `combineRange` im Zeitprotokoll (Live-Review 2026-09-28: vorher 0 h, auch im
 * Monats-PDF und bei der Übernahme in die Zeiterfassung). 0 bei fehlender,
 * ungültiger oder gleicher Zeit. Reine Funktion (FE+BE).
 */
export function hhmmDiffHours(start?: string | null, end?: string | null): number {
  if (!start || !end) return 0;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const raw = eh * 60 + em - (sh * 60 + sm);
  if (!Number.isFinite(raw)) return 0;
  const minutes = raw < 0 ? raw + 24 * 60 : raw;
  return minutes > 0 ? Math.round((minutes / 60) * 100) / 100 : 0;
}

export const tripDayInputSchema = z.object({
  date: z.string().date(),
  destination: z.string().trim().min(1).max(200),
  purpose: z.string().trim().min(1).max(200),
  startTime: timeString.optional(),
  endTime: timeString.optional(),
  borderOut: timeString.optional(),
  borderBack: timeString.optional(),
  lunchProvided: z.boolean().default(false),
  dinnerProvided: z.boolean().default(false),
});
export type TripDayInput = z.infer<typeof tripDayInputSchema>;

export const createTripSchema = z.object({
  title: z.string().trim().min(1).max(200),
  country: z.string().trim().min(1).max(120),
  days: z.array(tripDayInputSchema).min(1, 'Mindestens ein Reisetag erforderlich.'),
  /**
   * Reise für einen anderen Mitarbeiter erfassen (Admin/Sekretariat+) statt für
   * sich selbst. Serverseitig nur mit `travel.manage` erlaubt — ohne dieses
   * Feld bleibt es wie bisher der eigene, aus dem Login abgeleitete Mitarbeiter.
   */
  employeeId: z.string().uuid().optional(),
});
export type CreateTripInput = z.infer<typeof createTripSchema>;

export const updateTripSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  country: z.string().trim().min(1).max(120).optional(),
  days: z.array(tripDayInputSchema).min(1).optional(),
});
export type UpdateTripInput = z.infer<typeof updateTripSchema>;

export const tripDayResponseSchema = z.object({
  id: z.string().uuid(),
  date: z.string().datetime(),
  destination: z.string(),
  purpose: z.string(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  borderOut: z.string().nullable(),
  borderBack: z.string().nullable(),
  lunchProvided: z.boolean(),
  dinnerProvided: z.boolean(),
  /** Aus Von-/Bis-Zeit berechnete Stunden. */
  hours: z.number(),
});
export type TripDayResponse = z.infer<typeof tripDayResponseSchema>;

export const tripResponseSchema = z.object({
  id: z.string().uuid(),
  employeeId: z.string().uuid(),
  employeeName: z.string(),
  title: z.string(),
  country: z.string(),
  status: travelStatusSchema,
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
  /** Summe der Tagesstunden. */
  totalHours: z.number(),
  days: z.array(tripDayResponseSchema),
  reviewerName: z.string().nullable(),
  reviewedAt: z.string().datetime().nullable(),
  adminNote: z.string().nullable(),
  /** Gesetzt, sobald diese Reise in Zeiteinträge gewandelt wurde (einmalig). */
  convertedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TripResponse = z.infer<typeof tripResponseSchema>;

/**
 * Reise → Zeiterfassung wandeln (Anweisung Tech-Lead 2026-08-20): EINE Firma/
 * Kategorie/Projekt/Tätigkeit für die ganze Reise (nicht je Tag — eine Reise
 * hat noch keine dieser Dimensionen). Stunden je Tag sind die vom Nutzer
 * geprüften/angepassten Werte (Vorschlag aus `TripDayResponse.hours`, im
 * Frontend editierbar) — der Server übernimmt sie unverändert, er berechnet
 * nichts neu. Tage mit 0 Stunden werden beim Anlegen übersprungen (kein
 * leerer Zeiteintrag).
 */
export const convertTripDayInputSchema = z.object({
  tripDayId: z.string().uuid(),
  hours: z.number().min(0).max(24),
});
export type ConvertTripDayInput = z.infer<typeof convertTripDayInputSchema>;

export const convertTripSchema = z.object({
  legalEntityId: z.string().uuid(),
  categoryId: z.string().uuid().optional(),
  projectKind: timerProjectKindSchema,
  projectId: z.string().uuid(),
  ttSubCategoryId: z.string().uuid().optional(),
  days: z.array(convertTripDayInputSchema).min(1, 'Mindestens ein Reisetag erforderlich.'),
});
export type ConvertTripInput = z.infer<typeof convertTripSchema>;

export const convertTripResultSchema = z.object({
  createdCount: z.number().int(),
  skippedCount: z.number().int(),
});
export type ConvertTripResult = z.infer<typeof convertTripResultSchema>;

/** Genehmigen/Ablehnen mit optionaler Admin-Notiz. */
export const reviewTripSchema = z.object({
  adminNote: z.string().trim().max(2000).optional(),
});
export type ReviewTripInput = z.infer<typeof reviewTripSchema>;

/** Listen-Query (eigene + Admin): Pagination + Filter (Status/Mitarbeiter/Monat). */
export const tripsQuerySchema = listQuerySchema.extend({
  status: travelStatusSchema.optional(),
  employeeId: z.string().uuid().optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
});
export type TripsQuery = z.infer<typeof tripsQuerySchema>;
